import BaseViewExtensionViewFactory from '@/Factories/ViewFactories/BaseViewExtensionViewFactory';
import ViewFactory from '@/Factories/ViewFactory';
import AsyncViewFactory from '@/Factories/AsyncViewFactory';
import ComponentManager from '@/Management/ComponentManager';
import { IViewFactoryConstructorParameter } from '#/CoreFrameInterfaces';
import AsyncTypeGuards from '@/Utility/AsyncTypeGuards';
import ViewFactoryTuple from '@/Factories/ViewFactoryTuple';

export default class ExtensionViewFactory extends AsyncViewFactory {
    private readonly innerFactory: ViewFactory;

    constructor(params: IViewFactoryConstructorParameter) {
        super(params);

        this.innerFactory = this.createFactory(params) ?? this.createFallbackFactory(params);
        this.innerFactory.id = this.id;
    }

    public refresh(): boolean {
        return this.innerFactory.refresh();
    }

    public shouldReplaceRegion(): boolean {
        return this.innerFactory.shouldReplaceRegion();
    }

    public getRegion(): string {
        return this.innerFactory.getRegion();
    }

    public getView(): JQuery<HTMLElement> {
        return this.innerFactory.getView();
    }

    public getTuple(): ViewFactoryTuple {
        return this.innerFactory.getTuple();
    }

    public async getElementAsync(): Promise<JQuery> {
        return AsyncTypeGuards.isAsyncElementProvider(this.innerFactory)
            ? await this.innerFactory.getElementAsync()
            : this.innerFactory.getView();
    }

    public async getTupleAsync(): Promise<ViewFactoryTuple> {
        return AsyncTypeGuards.isAsyncTupleProvider<ViewFactoryTuple>(this.innerFactory)
            ? await this.innerFactory.getTupleAsync()
            : this.innerFactory.getTuple();
    }

    private createFactory(params: IViewFactoryConstructorParameter): ViewFactory {
        const innerFactoryName = params.Factory || params.factory;
        if (!innerFactoryName)
            return null;

        const component = ComponentManager.getFactory<typeof ViewFactory>(innerFactoryName);
        if (!component)
            return null;
        
        return new component(params);
    }
    
    private createFallbackFactory(params: IViewFactoryConstructorParameter): ViewFactory {
        const newparams = $.extend({ 
            viewElementName: 'ViewExtension', 
            viewModelName: 'ViewExtensionViewModel', 
            viewName: 'ViewExtensionView' 
        }, params);
        return new BaseViewExtensionViewFactory(newparams);
    }

    protected disposeInternal() {
        this.innerFactory?.dispose();

        super.disposeInternal();
    }

    public disposeView() {
        this.innerFactory?.disposeView();

        super.disposeView();
    }
}

ComponentManager.setFactory('ExtensionView', ExtensionViewFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');